/**
 * E5-M2: Send-Time Intelligence — real module backed by the intelligence service.
 *
 * Calls /api/email-marketing/marketing/send-time and renders:
 * - Optimal send hour recommendation
 * - Confidence level (high / medium / low)
 * - 24-hour engagement distribution bar chart (only when real data exists)
 * - Provenance message explaining data sufficiency
 *
 * No fake heatmaps. When data is insufficient the UI says so honestly.
 */
import { Fragment } from "react";
import { useApi } from "@/hooks/use-api";
import { Clock, AlertTriangle, BarChart3, CheckCircle2 } from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

// -------------------------------------------------------------------------
// Types — mirrors the API response serializer
// -------------------------------------------------------------------------

interface SendTimeRecommendation {
  optimalHour: number;
  confidence: "high" | "medium" | "low";
  distribution: Array<{ hour: number; score: number }>;
}

interface SendTimeProvenance {
  dataStatus: "sufficient" | "insufficient" | "none";
  daysSinceFirstTouch: number | null;
  message: string;
}

interface SendTimeData {
  recommendation: SendTimeRecommendation | null;
  heatmap: Array<{
    day: number;
    hour: number;
    deliveredCount: number;
    openCount: number;
    openRate: number;
  }>;
  provenance: SendTimeProvenance;
}

// -------------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------------

function formatHour(hour: number): string {
  const period = hour >= 12 ? "PM" : "AM";
  const display = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${display}:00 ${period}`;
}

const CONFIDENCE_CONFIG = {
  high: {
    label: "High confidence",
    description: "Strong engagement signal from 50+ interactions concentrated in clear time windows.",
    icon: CheckCircle2,
    color: "text-[var(--color-success)]",
    bg: "bg-[var(--color-success)]/10",
    border: "border-[var(--color-success)]/30",
  },
  medium: {
    label: "Medium confidence",
    description: "Moderate engagement signal. Recommendations will sharpen as more contacts interact.",
    icon: BarChart3,
    color: "text-[var(--color-warning)]",
    bg: "bg-[var(--color-warning)]/10",
    border: "border-[var(--color-warning)]/30",
  },
  low: {
    label: "Low confidence",
    description: "Limited engagement data. Using broad patterns. Keep sending to build a stronger signal.",
    icon: AlertTriangle,
    color: "text-[var(--color-muted-foreground)]",
    bg: "bg-[var(--color-surface)]",
    border: "border-[var(--color-border)]",
  },
} as const;

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

// -------------------------------------------------------------------------
// Sub-components
// -------------------------------------------------------------------------

function DistributionChart({ distribution }: { distribution: SendTimeRecommendation["distribution"] }) {
  const maxScore = Math.max(...distribution.map((d) => d.score), 0.01);

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
      <h2 className="text-sm font-medium text-[var(--color-foreground)] mb-4">Engagement Distribution (24h)</h2>
      <div className="flex items-end gap-1 h-40">
        {distribution.map((entry) => {
          const heightPct = Math.max(2, (entry.score / maxScore) * 100);
          const isTop = entry.score === maxScore && entry.score > 0;
          return (
            <div key={entry.hour} className="stagger-item flex-1 flex flex-col items-center gap-1">
              <div
                className={`w-full rounded-t transition-all ${
                  isTop
                    ? "bg-[var(--color-primary)]"
                    : "bg-[var(--color-primary)]/30"
                }`}
                style={{ height: `${heightPct}%` }}
                title={`${formatHour(entry.hour)}: ${(entry.score * 100).toFixed(1)}% of engagements`}
              />
              {entry.hour % 4 === 0 && (
                <span className="text-[10px] text-[var(--color-muted-foreground)]">
                  {entry.hour}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-[var(--color-muted-foreground)] mt-2">
        Each bar represents the share of successful opens grouped by send hour.
      </p>
    </div>
  );
}

function HeatmapGrid({ heatmap }: { heatmap: SendTimeData["heatmap"] }) {
  const cellsByKey = new Map(
    heatmap.map((cell) => [`${cell.day}:${cell.hour}`, cell] as const),
  );
  const maxOpenRate = heatmap.reduce((max, cell) => Math.max(max, cell.openRate), 0);

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium text-[var(--color-foreground)]">Open-Rate Heatmap</h2>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            Real delivered/opened campaign sends grouped by weekday and hour.
          </p>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-[var(--color-muted-foreground)]">
          <span>Lower</span>
          <div className="flex gap-1">
            {[0.15, 0.35, 0.6, 0.9].map((opacity) => (
              <span
                key={opacity}
                className="h-3 w-3 rounded-sm bg-[var(--color-primary)]"
                style={{ opacity }}
              />
            ))}
          </div>
          <span>Higher</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <div className="grid min-w-[44rem] grid-cols-[4rem_repeat(24,minmax(0,1fr))] gap-1">
          <div />
          {Array.from({ length: 24 }, (_, hour) => (
            <div key={`hour-${hour}`} className="text-center text-[10px] text-[var(--color-muted-foreground)]">
              {hour}
            </div>
          ))}
          {WEEKDAY_LABELS.map((dayLabel, day) => (
            <Fragment key={dayLabel}>
              <div
                className="flex items-center text-[10px] font-medium text-[var(--color-muted-foreground)]"
              >
                {dayLabel}
              </div>
              {Array.from({ length: 24 }, (_, hour) => {
                const cell = cellsByKey.get(`${day}:${hour}`);
                const intensity = cell && maxOpenRate > 0
                  ? Math.max(0.12, cell.openRate / maxOpenRate)
                  : 0.04;
                const title = cell
                  ? `${dayLabel} ${hour}:00 — ${(cell.openRate * 100).toFixed(1)}% open rate (${cell.openCount}/${cell.deliveredCount})`
                  : `${dayLabel} ${hour}:00 — no sends yet`;
                return (
                  <div
                    key={`${dayLabel}-${hour}`}
                    className="stagger-item h-7 rounded-md border border-[var(--color-border)]/50 bg-[var(--color-primary)] transition-transform hover:scale-[1.04]"
                    style={{ opacity: intensity }}
                    title={title}
                  />
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

function RecommendationCard({ recommendation }: { recommendation: SendTimeRecommendation }) {
  const conf = CONFIDENCE_CONFIG[recommendation.confidence];
  const ConfIcon = conf.icon;

  return (
    <div className={`rounded-xl border ${conf.border} ${conf.bg} p-5`}>
      <div className="flex items-start gap-3">
        <ConfIcon className={`h-5 w-5 mt-0.5 shrink-0 ${conf.color}`} />
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium uppercase tracking-wide ${conf.color}`}>
              {conf.label}
            </span>
          </div>
          <p className="text-sm text-[var(--color-muted-foreground)]">{conf.description}</p>
        </div>
      </div>
    </div>
  );
}

function NoDataState({ provenance }: { provenance: SendTimeProvenance }) {
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-8 text-center">
      <Clock className="h-10 w-10 text-[var(--color-muted-foreground)] mx-auto mb-3" />
      <h2 className="text-lg font-semibold text-[var(--color-foreground)]">
        {provenance.dataStatus === "none"
          ? "No Engagement Data Yet"
          : "Building Your Send-Time Profile"}
      </h2>
      <p className="text-sm text-[var(--color-muted-foreground)] mt-2 max-w-md mx-auto">
        {provenance.message}
      </p>
      {provenance.dataStatus === "insufficient" && provenance.daysSinceFirstTouch !== null && (
        <div className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[var(--color-surface)] px-3 py-2">
          <span className="text-xs text-[var(--color-muted-foreground)]">
            {provenance.daysSinceFirstTouch} / 90 days of data collected
          </span>
          <div className="w-24 h-1.5 bg-[var(--color-border)] rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--color-primary)] rounded-full transition-all"
              style={{ width: `${Math.min(100, (provenance.daysSinceFirstTouch / 90) * 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// Page
// -------------------------------------------------------------------------

export default function SendTimePage() {
  const { data, isLoading, error } = useApi<SendTimeData>(
    "/api/email-marketing/marketing/send-time/heatmap",
  );
  const heatmap = data?.heatmap ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Send-Time Intelligence</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Optimal send times computed from real engagement signals.
        </p>
      </div>

      {isLoading && (
        <div className="space-y-4">
          <SkeletonCard className="h-24 rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5 p-4">
          <p className="text-sm text-[var(--color-destructive)]">
            Failed to load send-time data. Please try again later.
          </p>
        </div>
      )}

      {data && !data.recommendation && (
        <>
          <NoDataState provenance={data.provenance} />
          {heatmap.length > 0 && <HeatmapGrid heatmap={heatmap} />}
        </>
      )}

      {data?.recommendation && (
        <>
          {/* Optimal hour card */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
              <div className="text-xs text-[var(--color-muted-foreground)] uppercase tracking-wide">
                Recommended Send Time
              </div>
              <div className="text-2xl font-semibold text-[var(--color-foreground)] mt-2 flex items-center gap-2">
                <Clock className="h-5 w-5 text-[var(--color-primary)]" />
                {formatHour(data.recommendation.optimalHour)} UTC
              </div>
            </div>
            <RecommendationCard recommendation={data.recommendation} />
          </div>

          {/* Provenance */}
          <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3">
            <p className="text-xs text-[var(--color-muted-foreground)]">
              {data.provenance.message}
            </p>
          </div>

          {/* Distribution chart — only shown when there is real data behind it */}
          <DistributionChart distribution={data.recommendation.distribution} />
          {heatmap.length > 0 && <HeatmapGrid heatmap={heatmap} />}
        </>
      )}
    </div>
  );
}
